import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ban, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useUpdateVisitStatus } from "@/hooks/useVisitManagement";

interface Visit {
  id: string;
  visit_type: string;
  visit_number: number;
  scheduled_date: string;
}

interface CancelVisitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visit: Visit;
  onSuccess?: () => void;
}

const CANCEL_REASONS = [
  { value: "patient_withdrew", label: "Patient withdrew consent" },
  { value: "protocol_amendment", label: "Protocol amendment (visit no longer required)" },
  { value: "site_decision", label: "Site decision" },
  { value: "sponsor_decision", label: "Sponsor decision" },
  { value: "adverse_event", label: "Adverse event (patient unable to attend)" },
  { value: "patient_ineligible", label: "Patient no longer eligible" },
  { value: "duplicate_booking", label: "Duplicate booking" },
  { value: "other", label: "Other (specify below)" },
] as const;

export function CancelVisitDialog({ 
  open, 
  onOpenChange, 
  visit,
  onSuccess 
}: CancelVisitDialogProps) {
  const [reason, setReason] = useState<string>("");
  const [details, setDetails] = useState("");
  const { mutate: updateVisit, isPending } = useUpdateVisitStatus();

  const handleSubmit = () => {
    if (!reason) return;
    if (reason === "other" && details.trim().length < 5) return;

    const reasonLabel = CANCEL_REASONS.find(r => r.value === reason)?.label || reason;
    const fullNotes = details.trim() 
      ? `CANCELLED - ${reasonLabel}: ${details.trim()}`
      : `CANCELLED - ${reasonLabel}`;

    updateVisit(
      {
        visitId: visit.id,
        status: 'cancelled',
        notes: fullNotes
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setReason("");
          setDetails("");
          onSuccess?.();
        }
      }
    );
  };

  const visitLabel = visit.visit_type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  const isValid = reason && (reason !== "other" || details.trim().length >= 5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-muted-foreground" />
            Cancel Visit
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="rounded-lg bg-muted p-3">
            <p className="text-sm text-muted-foreground">
              <strong>{visitLabel}</strong> (Visit #{visit.visit_number})
            </p>
            <p className="text-sm text-muted-foreground">
              Scheduled: {format(new Date(visit.scheduled_date), 'MMMM d, yyyy')}
            </p>
          </div>

          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-sm text-destructive font-medium">
              This action cannot be undone. The visit will be permanently marked as cancelled.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Cancellation Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border z-50">
                {CANCEL_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="cancel-details">
              Additional Details {reason === "other" ? "*" : "(Optional)"}
            </Label>
            <Textarea
              id="cancel-details"
              placeholder={reason === "other" 
                ? "Please describe the reason for cancellation..." 
                : "Any additional context about the cancellation..."
              }
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
            />
            {reason === "other" && details.trim().length < 5 && (
              <p className="text-xs text-destructive">
                Please provide at least 5 characters when selecting "Other"
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Go Back
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleSubmit} 
            disabled={!isValid || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cancelling...
              </>
            ) : (
              "Cancel Visit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
